import React from 'react';
import { ExternalLink, ShieldCheck, ArrowRight, Building, Award, CheckCircle2 } from 'lucide-react';
import { PARTNERS_LIST, PartnerItem } from '../data/siteData';

interface PartnersSectionProps {
  onOpenBooking: () => void;
}

export const PartnersSection: React.FC<PartnersSectionProps> = ({ onOpenBooking }) => {
  return (
    <section id="partners" className="py-20 lg:py-28 bg-[#080d11] text-white border-b border-slate-800 relative overflow-hidden">
      {/* Background Subtle Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-20 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 text-left">
          <div className="max-w-2xl">
            <div className="text-xs font-mono font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
              Ecosystem &amp; Commercial Alliances
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-serif-heading">
              Trusted by high-growth <br className="hidden sm:block" />
              enterprises &amp; institutions.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed font-normal">
              From renewable solar pioneers and corporate consulting groups to pan-African agricultural supply chains and non-profit foundations.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onOpenBooking}
              className="px-5 py-2.5 rounded-lg bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer shadow-md"
            >
              <span>Partner with Thinkers Arcade</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Partners Showcase Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
          {PARTNERS_LIST.map((partner) => (
            <div
              key={partner.id}
              className="rounded-2xl bg-slate-900/90 border border-slate-800 p-6 flex flex-col justify-between hover:border-[#b88e4c]/50 hover:bg-slate-900 transition-all duration-200 group"
            >
              <div>
                {/* Logo Mark Bar */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                  <span className="font-mono text-xs uppercase font-bold tracking-wider text-[#b88e4c]">
                    {partner.badge || 'Partner'}
                  </span>
                  {partner.domain && (
                    <a
                      href={`https://${partner.domain}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-400 hover:text-[#009fe3] transition-colors flex items-center gap-1 text-xs font-mono"
                    >
                      <span>{partner.domain}</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>

                {/* Monogram Brand Header */}
                <div className="mb-3">
                  <div className="text-base font-extrabold text-white tracking-wide group-hover:text-[#b88e4c] transition-colors font-display">
                    {partner.logoText}
                  </div>
                  <div className="text-xs text-slate-400 font-medium mt-0.5">
                    {partner.name}
                  </div>
                </div>

                <p className="text-xs text-slate-300 leading-relaxed">
                  {partner.description}
                </p>
              </div>

              <div className="pt-4 mt-4 border-t border-slate-800/80 flex items-center justify-between text-xs font-mono text-slate-400">
                <span className="text-slate-500">{partner.category}</span>
                <span className="text-slate-400 group-hover:text-white transition-colors">Verified</span>
              </div>
            </div>
          ))}
        </div>

        {/* Wall Street Forbes Credibility Banner */}
        <div className="mt-14 rounded-2xl bg-slate-950 border border-slate-800 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-left">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#009fe3]" />
              <span className="text-xs font-mono font-bold uppercase text-[#b88e4c]">
                Pan-African Digital Infrastructure
              </span>
            </div>
            <h4 className="text-base sm:text-lg font-bold text-white font-serif-heading">
              Developed for the African market, engineered for the world.
            </h4>
            <p className="text-xs text-slate-400">
              Direct technical inquiries and enterprise architecture consultations route directly to <strong className="text-white">deals@thinkersarcade.co.ke</strong>.
            </p>
          </div>

          <a
            href="mailto:deals@thinkersarcade.co.ke"
            className="rounded-lg bg-white hover:bg-slate-100 text-slate-950 font-bold px-5 py-2.5 text-xs whitespace-nowrap cursor-pointer transition-colors shadow-sm font-sans"
          >
            Direct Inquiries →
          </a>
        </div>

      </div>
    </section>
  );
};
